import { Form, FormValues, Input, Spin, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import TemplateSelect from '../../../templates/components/template-select/template-select';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import {
  useCreateClientGroupMutation,
  useGetClientGroupQuery,
  useUpdateClientGroupMutation,
} from '../../api/client-groups-api';
import { clientGroupFormModalId, clientGroupsAlertId } from '../../constants/constants';
import { ClientGroupFormModel } from '../../models/client-group-form-model';
import { selectActiveClientGroupId, setActiveClientGroupId } from '../../store/client-groups-slice';
import ClientUsersSelect from '../../../client-users/components/client-users-select/client-users-select';
import CustomersSelect from '../../../customers/components/customers-select/customers-select';

const ClientGroupFormModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['client-groups', 'common']);
  const dispatch = useAppDispatch();
  const activeClientGroupId = useAppSelector(selectActiveClientGroupId);

  const [form] = Form.useForm<ClientGroupFormModel>();

  const { data: clientGroupData, isLoading: isClientGroupLoading } = useGetClientGroupQuery(activeClientGroupId || '', {
    skip: !activeClientGroupId,
  });

  const [postClientGroupCombine, { isLoading: isCreateClientGroupLoading }] = useCreateClientGroupMutation();
  const [editClientGroupCombine, { isLoading: isUpdateClientGroupLoading }] = useUpdateClientGroupMutation();

  const closeClientGroupFormModal = () => {
    dispatch(closeModal(clientGroupFormModalId));
    dispatch(setActiveClientGroupId(null));
    form.resetFields();
  };

  const submitForm = (values: FormValues<typeof form>) => {
    (activeClientGroupId
      ? editClientGroupCombine({
          id: activeClientGroupId,
          name: values.name,
          templates: values.templates,
          users: values.users,
          customers: values.customers,
        })
      : postClientGroupCombine({
          name: values.name,
          templates: values.templates,
          users: values.users,
          customers: values.customers,
        })
    )
      .unwrap()
      .then(() => {
        closeClientGroupFormModal();
        dispatch(
          setAlert({
            id: clientGroupsAlertId,
            type: 'success',
            title: activeClientGroupId ? t('groupForm.messages.editSuccess') : t('groupForm.messages.createSuccess'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(form, error.data?.errors || { name: [t('errors.unexpected', { ns: 'common' })] });
      });
  };

  const initialValues: Pick<ClientGroupFormModel, 'name' | 'users' | 'templates'> = useMemo(() => {
    return {
      name: clientGroupData?.name || '',
      templates: clientGroupData?.templates.map(({ id }) => id) || [],
      users: clientGroupData?.users.map(({ id }) => id) || [],
      customers:
        clientGroupData?.customers.map(({ id, name, surname, email }) => ({
          value: id,
          label: (
            <>
              {name} {surname} – <Typography.Text italic>{email}</Typography.Text>
            </>
          ),
        })) || [],
    };
  }, [clientGroupData]);

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Modal
      title={activeClientGroupId ? t('groupForm.editGroup') : t('groupForm.createGroup')}
      onCancel={() => (!isCreateClientGroupLoading || !isUpdateClientGroupLoading) && closeClientGroupFormModal()}
      isLoading={isCreateClientGroupLoading || isUpdateClientGroupLoading || isClientGroupLoading}
      onOk={() => form.submit()}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <Spin spinning={isClientGroupLoading}>
        <Form form={form} initialValues={initialValues} layout="vertical" onFinish={submitForm}>
          <Form.Item
            name="name"
            label={t('label.name', { ns: 'common' })}
            rules={[{ required: true, message: t('errors.nameRequired', { ns: 'common' }) }]}
          >
            <Input placeholder={t('label.name', { ns: 'common' })} />
          </Form.Item>
          <Form.Item label={t('label.templates', { ns: 'common' })} name="templates">
            <TemplateSelect mode="multiple" maxTagCount={3} />
          </Form.Item>
          <Form.Item label={t('label.users', { ns: 'common' })} name="users">
            <ClientUsersSelect mode="multiple" maxTagCount={3} />
          </Form.Item>
          <Form.Item name="customers" label={t('label.customers', { ns: 'common' })}>
            <CustomersSelect
              mode="multiple"
              maxTagCount={3}
              placeholder={t('signScanRequest.placeholder.customer', { ns: 'common' })}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

const ClientGroupFormModal = (): JSX.Element => {
  return <LazyModal modalId={clientGroupFormModalId} Component={ClientGroupFormModalContent} />;
};

export default ClientGroupFormModal;
